/**
 * Turns the Random Dataset Generator's form fields into a dataset, or says which field
 * is wrong. Kept out of the widget so the rules (and the error each breaks) are tested.
 */

import {
  parseIntegerInput,
  parseNumberInput,
  parseNumberList,
} from "@/lib/parse-number";
import {
  type Column,
  type Dataset,
  groupValues,
  idColumn,
  type NumberColumn,
  pairValues,
  type SingleDistribution,
  singleValues,
} from "./dataset";

export type DatasetMode = "single" | "pair" | "groups";

export interface DatasetForm {
  mode: DatasetMode;
  /** Rows, or rows per group in the groups mode. */
  rows: string;
  seed: string;
  decimals: string;
  exact: boolean;
  includeId: boolean;
  distribution: SingleDistribution;
  p1: string;
  p2: string;
  valueName: string;
  nameX: string;
  meanX: string;
  sdX: string;
  nameY: string;
  meanY: string;
  sdY: string;
  r: string;
  groupColumn: string;
  outcomeColumn: string;
  groupNames: string;
  groupMeans: string;
  groupSd: string;
}

/** Column names used when a name field is left empty (the widget supplies the words). */
export interface DefaultNames {
  id: string;
  value: string;
  x: string;
  y: string;
  group: string;
  outcome: string;
}

export type DatasetError =
  | "rows"
  | "groupRows"
  | "seed"
  | "decimals"
  | "normal"
  | "uniform"
  | "skewed"
  | "integer"
  | "binary"
  | "pairSd"
  | "pairMean"
  | "correlation"
  | "names"
  | "groupNames"
  | "groupMeans"
  | "groupSd";

export interface DatasetInfo {
  mode: DatasetMode;
  targetR: number | null;
  exact: boolean;
  seed: string;
  groupNames: string[];
}

export type BuildResult =
  | { ok: true; dataset: Dataset; info: DatasetInfo }
  | { ok: false; error: DatasetError };

export const MIN_ROWS = 3;
export const MAX_ROWS = 10_000;
export const MAX_GROUPS = 12;
const MAX_DECIMALS = 8;
const LIST_SPLIT = /[,;\n]+/;

function fail(error: DatasetError): BuildResult {
  return { ok: false, error };
}

function numberColumn(
  name: string,
  values: number[],
  decimals: number
): NumberColumn {
  return { kind: "number", name, values, decimals };
}

interface Shared {
  n: number;
  seed: string;
  decimals: number;
  exact: boolean;
  idName: string | null;
}

function withId(columns: Column[], shared: Shared, rows: number): Column[] {
  return shared.idName === null
    ? columns
    : [idColumn(shared.idName, rows), ...columns];
}

function buildSingle(
  form: DatasetForm,
  shared: Shared,
  names: DefaultNames
): BuildResult {
  const p1 = parseNumberInput(form.p1);
  const p2 = parseNumberInput(form.p2);
  const { distribution } = form;
  if (p1 === null || (distribution !== "binary" && p2 === null)) {
    return fail(distribution);
  }
  const second = p2 ?? 0;
  const invalid: Record<SingleDistribution, boolean> = {
    normal: second <= 0,
    uniform: second <= p1,
    skewed: second <= p1,
    integer: !(Number.isInteger(p1) && Number.isInteger(second)) || second < p1,
    binary: p1 < 0 || p1 > 1,
  };
  if (invalid[distribution]) {
    return fail(distribution);
  }
  const values = singleValues(
    distribution,
    p1,
    second,
    shared.n,
    shared.seed,
    shared.exact && distribution === "normal"
  );
  const whole = distribution === "integer" || distribution === "binary";
  const column = numberColumn(
    form.valueName.trim() || names.value,
    values,
    whole ? 0 : shared.decimals
  );
  return {
    ok: true,
    dataset: { columns: withId([column], shared, shared.n), rows: shared.n },
    info: {
      mode: "single",
      targetR: null,
      exact: shared.exact && distribution === "normal",
      seed: shared.seed,
      groupNames: [],
    },
  };
}

function buildPair(
  form: DatasetForm,
  shared: Shared,
  names: DefaultNames
): BuildResult {
  const meanX = parseNumberInput(form.meanX);
  const meanY = parseNumberInput(form.meanY);
  const sdX = parseNumberInput(form.sdX);
  const sdY = parseNumberInput(form.sdY);
  const r = parseNumberInput(form.r);
  if (meanX === null || meanY === null) {
    return fail("pairMean");
  }
  if (sdX === null || sdY === null || sdX <= 0 || sdY <= 0) {
    return fail("pairSd");
  }
  if (r === null || r <= -1 || r >= 1) {
    return fail("correlation");
  }
  const nameX = form.nameX.trim() || names.x;
  const nameY = form.nameY.trim() || names.y;
  const [x, y] = pairValues(
    { meanX, sdX, meanY, sdY, r },
    shared.n,
    shared.seed,
    shared.exact
  );
  const columns = [
    numberColumn(nameX, x, shared.decimals),
    numberColumn(nameY, y, shared.decimals),
  ];
  return {
    ok: true,
    dataset: { columns: withId(columns, shared, shared.n), rows: shared.n },
    info: {
      mode: "pair",
      targetR: r,
      exact: shared.exact,
      seed: shared.seed,
      groupNames: [],
    },
  };
}

function buildGroups(
  form: DatasetForm,
  shared: Shared,
  names: DefaultNames
): BuildResult {
  const groupNames = form.groupNames
    .split(LIST_SPLIT)
    .map((name) => name.trim())
    .filter((name) => name !== "");
  if (
    groupNames.length < 2 ||
    groupNames.length > MAX_GROUPS ||
    new Set(groupNames).size !== groupNames.length
  ) {
    return fail("groupNames");
  }
  const means = parseNumberList(form.groupMeans);
  if (means.length !== groupNames.length) {
    return fail("groupMeans");
  }
  const sd = parseNumberInput(form.groupSd);
  if (sd === null || sd <= 0) {
    return fail("groupSd");
  }
  const total = shared.n * groupNames.length;
  if (total > MAX_ROWS) {
    return fail("groupRows");
  }
  const groupColumn = form.groupColumn.trim() || names.group;
  const outcomeColumn = form.outcomeColumn.trim() || names.outcome;
  const { labels, values } = groupValues(
    { names: groupNames, means, sd, perGroup: shared.n },
    shared.seed,
    shared.exact
  );
  const columns: Column[] = [
    { kind: "text", name: groupColumn, values: labels },
    numberColumn(outcomeColumn, values, shared.decimals),
  ];
  return {
    ok: true,
    dataset: { columns: withId(columns, shared, total), rows: total },
    info: {
      mode: "groups",
      targetR: null,
      exact: shared.exact,
      seed: shared.seed,
      groupNames,
    },
  };
}

export function buildDataset(
  form: DatasetForm,
  names: DefaultNames
): BuildResult {
  const n = parseIntegerInput(form.rows);
  if (n === null || n < MIN_ROWS || n > MAX_ROWS) {
    return fail("rows");
  }
  const seed = form.seed.trim();
  if (seed === "") {
    return fail("seed");
  }
  const decimals = parseIntegerInput(form.decimals);
  if (decimals === null || decimals < 0 || decimals > MAX_DECIMALS) {
    return fail("decimals");
  }
  const shared: Shared = {
    n,
    seed,
    decimals,
    exact: form.exact,
    idName: form.includeId ? names.id : null,
  };
  let result: BuildResult;
  if (form.mode === "pair") {
    result = buildPair(form, shared, names);
  } else if (form.mode === "groups") {
    result = buildGroups(form, shared, names);
  } else {
    result = buildSingle(form, shared, names);
  }
  if (result.ok) {
    const headers = result.dataset.columns.map((column) => column.name);
    if (new Set(headers).size !== headers.length) {
      return fail("names");
    }
  }
  return result;
}
